/**
 * Rename Warning Dialog Component
 * Warns users when renaming a category/subcategory that has assets using it
 */

import { Button } from '../ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { AlertTriangle, Image as ImageIcon } from 'lucide-react';
import type { AffectedAsset } from '../../types/api';

interface RenameWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: 'category' | 'subcategory';
  oldName: string;
  newName: string;
  affectedAssets: AffectedAsset[];
  assetCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

export function RenameWarningDialog({
  open,
  onOpenChange,
  entityType,
  oldName,
  newName,
  affectedAssets,
  assetCount,
  onConfirm,
  onCancel,
  isLoading,
}: RenameWarningDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[600px]">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/10">
              <AlertTriangle className="h-5 w-5 text-warning" />
            </div>
            <AlertDialogTitle>
              {assetCount} asset{assetCount === 1 ? ' is' : 's are'} using this {entityType}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            Renaming "{oldName}" to "{newName}" will update references for {assetCount} asset
            {assetCount === 1 ? '' : 's'}.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          <p className="text-sm font-medium">Affected assets:</p>
          <div className="max-h-[400px] overflow-y-auto space-y-2 rounded-md border p-3">
            {affectedAssets.map((asset) => (
              <div
                key={asset.asset_id}
                className="flex items-center gap-3 rounded-md border bg-card p-2"
              >
                <div className="flex h-16 w-auto items-center justify-center rounded overflow-hidden flex-shrink-0">
                  {asset.primary_photo_url ? (
                    <img
                      src={asset.primary_photo_url}
                      alt={asset.asset_nickname}
                      className="h-full w-auto"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-muted rounded flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{asset.asset_nickname}</p>
                  <p className="text-xs text-muted-foreground">#{asset.unique_identifier}</p>
                </div>
              </div>
            ))}
            {assetCount > 10 && (
              <div className="text-center py-2 text-sm text-muted-foreground">
                and {assetCount - 10} more asset{assetCount - 10 === 1 ? '' : 's'}...
              </div>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? 'Renaming...' : 'Rename Anyway'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
